/**
 * MilestoneAsiMount — a milestone's linked ASI, mounted inline under MilestoneSections
 * when the milestone's deliverable type is the ASI template (isAsiMilestoneType).
 *
 * The reverse direction (ASI -> milestone) is CaMilestoneMount in ConstructionSection.jsx,
 * which renders the canonical MilestoneRow inline under an expanded ASI. This is the other
 * side: expanding an ASI-type milestone reveals the canonical AsiNode exactly as it appears
 * in Reference > Construction (One-Component-Per-Node), mirroring ThreadCaMount/CaThreadMount's
 * relationship for RFIs/Submittals.
 *
 * Lookup: Supabase reverse query by milestone_airtable_id (getAsiByMilestone), cached in
 * ctx.milestoneAsiCache[msId] as the asi row or false (no match). The contract record comes
 * from ctx.contractCache (loaded on demand, same as ThreadCaMount).
 *
 * Renders null for milestone types that aren't the ASI template.
 */

import { useEffect, useRef } from 'react'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { isAsiMilestoneType } from '../outline/constants.js'
import { getAsiByMilestone } from '../services/asis.js'
import { getProjectContracts } from '../services/bidCampaigns.js'
import { AsiNode } from './ConstructionSection.jsx'

function ensureContractsLoaded(projId) {
  if (!projId) return
  if (ctx.contractCache !== undefined) return
  ctx.contractCache = null
  getProjectContracts(projId).then(c => {
    ctx.contractCache = c
    window.dispatchEvent(new Event('outline-render'))
  }).catch(() => {
    ctx.contractCache = []
    window.dispatchEvent(new Event('outline-render'))
  })
}

export default function MilestoneAsiMount({ ms, projectId }) {
  // The type check takes the Deliverable Type record ID since the milestone-type-ids change; passing
  // the type NAME made this always false, so no linked record ever showed (fixed 2026-09-24).
  const typeName = (ms?.fields?.['Deliverable'] || [])[0] || ''
  const msId = ms?.id || ''
  const isAsi = isAsiMilestoneType(typeName)
  const loadedRef = useRef(false)

  function fetchAsi() {
    if (!ctx.milestoneAsiCache) ctx.milestoneAsiCache = {}
    if (ctx.milestoneAsiCache[msId] !== undefined) return
    ctx.milestoneAsiCache[msId] = null // in-flight
    getAsiByMilestone(msId).then(asi => {
      if (!ctx.milestoneAsiCache) ctx.milestoneAsiCache = {}
      ctx.milestoneAsiCache[msId] = asi || false
      if (asi && asi.project_id) ensureContractsLoaded(asi.project_id)
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {
      if (!ctx.milestoneAsiCache) ctx.milestoneAsiCache = {}
      ctx.milestoneAsiCache[msId] = false
      window.dispatchEvent(new Event('outline-render'))
    })
  }

  // Lazy-load the linked ASI once per milestone (hooks before any return)
  useEffect(() => {
    if (!isAsi || !msId || loadedRef.current) return
    loadedRef.current = true
    fetchAsi()
  }, [isAsi, msId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch after a cache bust (e.g. onChanged from an edit made via this mount).
  useEffect(() => {
    if (!isAsi || !msId) return
    function onRender() {
      if (!loadedRef.current) return
      if (ctx.milestoneAsiCache?.[msId] === undefined) fetchAsi()
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [isAsi, msId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAsi || !msId) return null

  const entry = ctx.milestoneAsiCache?.[msId]
  if (entry === undefined || entry === null) return null // not started / in-flight — quiet
  if (entry === false) return null // no ASI linked to this milestone

  const asi = entry
  const contracts = Array.isArray(ctx.contractCache) ? ctx.contractCache : []
  const contract = contracts.find(c => c.id === asi.contract_id)
    || { id: asi.contract_id, project_id: asi.project_id || projectId || wf.zoomProjectId || '' }

  function onChanged() {
    if (ctx.milestoneAsiCache) ctx.milestoneAsiCache[msId] = undefined
    if (ctx.asiCache && asi.contract_id) ctx.asiCache[asi.contract_id] = undefined
    window.dispatchEvent(new Event('outline-render'))
  }

  return <AsiNode asi={asi} contract={contract} onChanged={onChanged} hideMilestoneMount labelPrefix="ASI: " mountCtx={'ms:' + msId} />
}
